import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Folder, MessageSquare, Clock, Image, User, Menu, LogOut, Users, BarChart3, Settings as SettingsIcon, Wrench, Mic, Home, Bot, Youtube, Bell, StickyNote, Camera, Calendar, Briefcase, Phone, History as HistoryIcon, ShieldCheck, Calculator, Trophy, MapPin, Palette } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';
import { Button } from './ui/button';
import axios from 'axios';
import { useUnreadNotes } from '../contexts/UnreadNotesContext';
import { ClientChatbot } from './ClientChatbot';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const Navigation = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadNotesCount, setUnreadNotesCount] = useState(0);
  const [hasNewPhotos, setHasNewPhotos] = useState(false);
  const [pendingServicesCount, setPendingServicesCount] = useState(0);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [chatbotOpen, setChatbotOpen] = useState(false);
  const [pendingUsersCount, setPendingUsersCount] = useState(0);
  const [scheduleAlertCount, setScheduleAlertCount] = useState(0);
  const [unreadCommsCount, setUnreadCommsCount] = useState(0);
  const [pendingPhotosCount, setPendingPhotosCount] = useState(0);
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);

  const fetchUnreadNotifCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread/count`);
      setUnreadNotifCount(response.data.unread_count || 0);
    } catch { /* silent */ }
  };
  const { refreshUnreadCount } = useUnreadNotes();

  // Fetch schedule alerts count
  const fetchScheduleAlerts = async () => {
    try {
      const response = await axios.get(`${API}/calendar/events/upcoming`, {
        params: { limit: 20 }
      });
      const events = response.data;
      
      if (user.role === 'admin' || user.role === 'supervisor') {
        // For admin: count pending requests
        const pendingRequests = events.filter(e => 
          (e.is_employee_request || e.is_client_request) && e.request_status === 'pending'
        );
        setScheduleAlertCount(pendingRequests.length);
      } else {
        // For others: count upcoming events in next 7 days
        const now = new Date();
        const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        const upcomingEvents = events.filter(e => {
          const eventDate = new Date(e.start_datetime);
          return eventDate >= now && eventDate <= weekFromNow;
        });
        setScheduleAlertCount(upcomingEvents.length);
      }
    } catch (error) {
      console.error('Error fetching schedule alerts:', error);
    }
  };

  useEffect(() => {
    fetchUnreadCount();
    fetchScheduleAlerts();
    fetchUnreadNotifCount();
    if (user.role === 'client') {
      fetchClientPhotoStatus();
      fetchUnreadMessagesCount(); // Clients can also receive messages from admins
    }
    if ((user.role === 'admin' || user.role === 'supervisor') || user.role === 'employee') {
      fetchPendingServicesCount();
      fetchUnreadMessagesCount();
      fetchUnreadCommsCount();
    }
    if ((user.role === 'admin' || user.role === 'supervisor')) {
      fetchPendingUsersCount();
      fetchPendingPhotosCount();
    }
  }, [refreshUnreadCount]);  // Refresh when context changes

  useEffect(() => {
    // Also refresh every 30 seconds
    const interval = setInterval(() => {
      fetchUnreadCount();
      fetchScheduleAlerts();
      fetchUnreadNotifCount();
      if (user.role === 'client') {
        fetchClientPhotoStatus();
        fetchUnreadMessagesCount();
      }
      if ((user.role === 'admin' || user.role === 'supervisor') || user.role === 'employee') {
        fetchPendingServicesCount();
        fetchUnreadMessagesCount();
        fetchUnreadCommsCount();
      }
      if ((user.role === 'admin' || user.role === 'supervisor')) {
        fetchPendingUsersCount();
        fetchPendingPhotosCount();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [user.role]);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notes/unread-count`);
      setUnreadNotesCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch unread notes count');
    }
  };

  const fetchClientPhotoStatus = async () => {
    try {
      const response = await axios.get(`${API}/photos/client/has-new`);
      setHasNewPhotos(response.data.has_new);
    } catch (error) {
      console.error('Failed to fetch client photo status');
    }
  };

  const fetchPendingServicesCount = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      const pendingCount = response.data.filter(s => s.service_status === 'Pending').length;
      setPendingServicesCount(pendingCount);
    } catch (error) {
      console.error('Failed to fetch pending services count');
    }
  };

  const fetchUnreadMessagesCount = async () => {
    try {
      const response = await axios.get(`${API}/direct-messages/unread/count`);
      setUnreadMessagesCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch unread messages count');
    }
  };

  const fetchPendingUsersCount = async () => {
    try {
      const response = await axios.get(`${API}/users/pending`);
      setPendingUsersCount(response.data?.length || 0);
    } catch (error) {
      console.error('Failed to fetch pending users count');
    }
  };

  const fetchUnreadCommsCount = async () => {
    try {
      const response = await axios.get(`${API}/communications/unread-count`);
      setUnreadCommsCount(response.data?.count || 0);
    } catch (error) {
      // silent
    }
  };

  const fetchPendingPhotosCount = async () => {
    try {
      const response = await axios.get(`${API}/photos/pending-count`);
      setPendingPhotosCount(response.data?.count || 0);
    } catch (error) {
      // silent
    }
  };

  // Bottom navigation items - filter based on role
  const allNavItems = [
    { path: '/client-dashboard', icon: Home, label: 'Home', roles: ['client'] }, // Client dashboard home
    { path: '/projects', icon: Folder, label: 'Projects', roles: ['admin', 'supervisor', 'employee', 'sub_contractor'] },
    { path: '/connections', icon: Phone, label: 'Connections', roles: ['admin', 'supervisor', 'employee'] },
    { path: '/messages', icon: MessageSquare, label: 'Messages', roles: ['client'] }, // Only for clients - admins/employees use header icon
    { path: '/timeclock', icon: Clock, label: 'Time', roles: ['admin', 'supervisor', 'employee'] }, // Removed sub_contractor
    { path: '/notifications', icon: Bell, label: 'Alerts', roles: ['admin', 'supervisor', 'employee', 'sub_contractor', 'client'] },
    { path: '/profile', icon: User, label: 'Profile', roles: ['admin', 'supervisor', 'employee', 'sub_contractor', 'client'] }
  ];

  const navItems = allNavItems.filter(item => item.roles.includes(user.role));

  // Menu items - filter based on role
  const allMenuItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'employee', 'sub_contractor'] },
    { path: '/calendar', icon: Calendar, label: 'Calendar', roles: ['admin', 'supervisor'] },
    { path: '/my-schedule', icon: Bell, label: 'My Schedule', roles: ['admin', 'supervisor', 'employee', 'client'] }, // Removed sub_contractor
    { path: '/projects', icon: Folder, label: 'Projects', roles: ['admin', 'employee', 'sub_contractor'] },
    { path: '/services', icon: Wrench, label: 'Services', roles: ['admin', 'employee'] },
    { path: '/site-walk', icon: Mic, label: 'AI Site Walk', roles: ['admin', 'employee'] },
    { path: '/photo-albums', icon: Camera, label: 'Photo Albums', roles: ['admin', 'employee', 'sub_contractor'] },
    { path: '/team', icon: Users, label: 'Team', roles: ['admin'] }, // Admin only
    { path: '/subcontractors', icon: Briefcase, label: 'Sub-Contractors', roles: ['admin'] }, // Admin only
    { path: '/tools', icon: Wrench, label: 'Tool Inventory', roles: ['admin'] }, // Admin only
    { path: '/notes-management', icon: StickyNote, label: 'Manage Notes', roles: ['admin'] }, // Admin only
    { path: '/hours-analytics', icon: BarChart3, label: 'Hours Analytics', roles: ['admin', 'supervisor'] }, // Admin/supervisor
    { path: '/my-analytics', icon: BarChart3, label: 'My Analytics', roles: ['admin', 'supervisor', 'employee'] }, // Personal stats for everyone with time entries
    { path: '/message-log', icon: HistoryIcon, label: 'Message Log', roles: ['admin', 'supervisor', 'employee', 'sub_contractor'] },
    { path: '/estimator', icon: Calculator, label: 'Estimator Tools', roles: ['admin', 'supervisor', 'employee'] },
    { path: '/job-map', icon: MapPin, label: 'Job Site Map', roles: ['admin', 'supervisor', 'employee'] },
    { path: '/leaderboard', icon: Trophy, label: 'Leaderboard', roles: ['admin', 'supervisor', 'employee'] },
    { path: '/photos/pending', icon: ShieldCheck, label: 'Pending Photo Approvals', roles: ['admin', 'supervisor'] },
    { path: '/appearance', icon: Palette, label: 'Appearance', roles: ['admin', 'supervisor', 'employee', 'sub_contractor', 'client'] },
    { path: '/reports', icon: BarChart3, label: 'Reports', roles: ['admin', 'employee'] }
  ];

  const menuItems = allMenuItems.filter(item => item.roles.includes(user.role));

  // Add Work Schedule and My Tools for employees only (not sub-contractors)
  if (user.role === 'employee') {
    menuItems.push({ path: '/my-tools', icon: Wrench, label: 'Shop Tools' });
    menuItems.push({ path: '/my-personal-tools', icon: Wrench, label: 'My Personal Tool Log' });
    menuItems.push({ path: '/work-schedule', icon: Bell, label: 'Work Alarms' });
    menuItems.push({ path: '/weekly-hours', icon: Clock, label: 'Weekly Hours' });
  }

  // Weekly Hours moved from bottom nav to Menu for admin/supervisor
  if (['admin', 'supervisor'].includes(user.role)) {
    menuItems.push({ path: '/weekly-hours', icon: Clock, label: 'Weekly Hours' });
  }

  // Add Settings and Video Settings for admin users
  if ((user.role === 'admin' || user.role === 'supervisor')) {
    menuItems.push({ path: '/admin/photo-albums', icon: Image, label: 'All Photo Albums' });
    menuItems.push({ path: '/video-settings', icon: Youtube, label: 'Video Settings' });
    menuItems.push({ path: '/settings', icon: SettingsIcon, label: 'Settings' });
  }

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed bottom-0 left-0 right-0 shadow-lg z-50" data-testid="bottom-navigation" style={{ position: 'fixed', background: 'var(--nav-bg, #2563eb)', borderTop: '1px solid var(--nav-border, #1d4ed8)' }}>
      <div className="max-w-7xl mx-auto px-2 sm:px-4">
        <div className="flex items-center justify-around" style={{ minHeight: '68px' }}>
          {navItems.map((item) => {
            const Icon = item.icon;
            const showBadge = (item.path === '/projects' && unreadNotesCount > 0) || 
                              (item.path === '/services' && pendingServicesCount > 0) ||
                              (item.path === '/messages' && unreadMessagesCount > 0) ||
                              (item.path === '/connections' && unreadCommsCount > 0) ||
                              (item.path === '/notifications' && unreadNotifCount > 0) ||
                              (item.path === '/my-schedule' && scheduleAlertCount > 0);
            const badgeContent = item.path === '/projects' ? unreadNotesCount : 
                                item.path === '/services' ? pendingServicesCount :
                                item.path === '/messages' ? unreadMessagesCount :
                                item.path === '/connections' ? unreadCommsCount :
                                item.path === '/notifications' ? unreadNotifCount :
                                item.path === '/my-schedule' ? scheduleAlertCount : '';
            const navBadgeColor = item.path === '/my-schedule' 
              ? (user.role === 'admin' || user.role === 'supervisor' ? 'bg-orange-500' : 'bg-blue-500')
              : 'bg-red-600';
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className="relative flex flex-col items-center justify-center gap-1 px-2 sm:px-4 py-2 rounded-lg transition-all min-h-[52px]"
                style={{
                  color: isActive(item.path) ? '#ffffff' : '#bfdbfe',
                  background: isActive(item.path) ? 'rgba(37, 99, 235, 0.3)' : 'transparent'
                }}
              >
                {item.path === '/profile' && user.profile_picture ? (
                  <img
                    src={user.profile_picture.startsWith('http') ? user.profile_picture : `${BACKEND_URL}${user.profile_picture}`}
                    alt={user.name}
                    data-testid="nav-profile-avatar"
                    className="w-6 h-6 rounded-full object-cover"
                    style={{ border: isActive(item.path) ? '2px solid #ffffff' : '2px solid #bfdbfe' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <Icon className="w-6 h-6" />
                )}
                <span className="text-xs font-semibold">{item.label}</span>
                {showBadge && (
                  <span className={`absolute top-0 right-0 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white ${navBadgeColor} rounded-full shadow-md`}>
                    {badgeContent}
                  </span>
                )}
              </button>
            );
          })}
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="nav-menu"
                className="relative flex flex-col items-center justify-center gap-1 px-2 sm:px-4 py-2 rounded-lg transition-all min-h-[52px]"
                style={{ color: '#bfdbfe' }}
              >
                <Menu className="w-6 h-6" />
                <span className="text-xs font-semibold">Menu</span>
                {/* Menu badge showing total alerts */}
                {(scheduleAlertCount > 0 || unreadNotesCount > 0 || pendingServicesCount > 0 || unreadMessagesCount > 0 || pendingUsersCount > 0 || unreadCommsCount > 0 || pendingPhotosCount > 0) && (
                  <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-red-500 rounded-full shadow-md animate-pulse">
                    {scheduleAlertCount + unreadNotesCount + pendingServicesCount + unreadMessagesCount + pendingUsersCount + unreadCommsCount + pendingPhotosCount}
                  </span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-white border-gray-200 shadow-xl rounded-lg">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const showMenuBadge = (item.path === '/projects' && unreadNotesCount > 0) || 
                                      (item.path === '/services' && pendingServicesCount > 0) ||
                                      (item.path === '/messages' && unreadMessagesCount > 0) ||
                                      (item.path === '/message-log' && unreadCommsCount > 0) ||
                                      (item.path === '/photos/pending' && pendingPhotosCount > 0) ||
                                      (item.path === '/settings' && pendingUsersCount > 0) ||
                                      (item.path === '/my-schedule' && scheduleAlertCount > 0);
                const menuBadgeContent = item.path === '/projects' ? unreadNotesCount : 
                                         item.path === '/services' ? pendingServicesCount :
                                         item.path === '/messages' ? unreadMessagesCount :
                                         item.path === '/message-log' ? unreadCommsCount :
                                         item.path === '/photos/pending' ? pendingPhotosCount :
                                         item.path === '/settings' ? pendingUsersCount :
                                         item.path === '/my-schedule' ? scheduleAlertCount : '';
                const badgeColor = item.path === '/my-schedule' 
                  ? (user.role === 'admin' || user.role === 'supervisor' ? 'bg-orange-500' : 'bg-blue-500')
                  : 'bg-red-600';
                return (
                  <DropdownMenuItem
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    data-testid={`menu-${item.label.toLowerCase().replace(' ', '-')}`}
                    className="cursor-pointer text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-4 py-3 text-base relative"
                  >
                    <Icon className="w-5 h-5 mr-3 text-blue-600" />
                    {item.label}
                    {showMenuBadge && (
                      <span className={`ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white ${badgeColor} rounded-full`}>
                        {menuBadgeContent}
                      </span>
                    )}
                  </DropdownMenuItem>
                );
              })}
              <DropdownMenuSeparator className="bg-gray-200 my-1" />
              <DropdownMenuItem
                onClick={onLogout}
                data-testid="logout-btn"
                className="cursor-pointer text-red-600 hover:text-red-700 hover:bg-red-50 px-4 py-3 text-base font-semibold"
              >
                <LogOut className="w-5 h-5 mr-3" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      {/* AI Chatbot - only show when open */}
      {chatbotOpen && (
        <div className="fixed inset-0 z-40 pointer-events-none">
          <div className="pointer-events-auto">
            <ClientChatbot 
              isOpen={chatbotOpen}
              onClose={() => setChatbotOpen(false)}
            />
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;